import { capitalizeAllLetters } from "Utils/utils";
import { buildTimesheetConfirmDetails } from "./timesheetConfirmDetails";

export const UNAPPROVE_BULK_STATUSES = ["approved", "rejected", "Expired"];
export const BULK_UNAPPROVE_MAX = 10;

export const getSelectableStatusForFilter = (filterValue) => {
  if (filterValue === "all" || filterValue === "pending") {
    return "pending";
  }
  if (UNAPPROVE_BULK_STATUSES.includes(filterValue)) {
    return filterValue;
  }
  return null;
};

export const isUnapproveBulkStatus = (status) =>
  UNAPPROVE_BULK_STATUSES.includes(String(status || ""));

export const isRowSelectableForFilter = (row, filterValue) => {
  const selectableStatus = getSelectableStatusForFilter(filterValue);
  if (!selectableStatus || !row) return false;
  if (selectableStatus === "pending") {
    return String(row.status || "").toLowerCase() === "pending";
  }
  return String(row.status || "") === selectableStatus;
};

export const countAmbiguousLinesFromAssessment = (assessData) => {
  if (Array.isArray(assessData?.timesheets) && assessData.timesheets.length > 0) {
    return assessData.timesheets.reduce(
      (sum, row) => sum + (Number(row?.ambiguous_line_count) || 0),
      0
    );
  }

  const first = assessData?.ambiguous_timesheets?.[0];
  return first?.ambiguous_lines?.length ?? 0;
};

export const assessmentRequiresFundingWarning = (assessData) =>
  Boolean(assessData?.requires_warning);

export const buildWorkerLabelForConfirm = (row) => {
  const worker = row?.worker;
  if (!worker) return "Worker not listed";
  return capitalizeAllLetters(
    `${worker.first_name || ""} ${worker.last_name || ""}`.trim()
  );
};

export const buildBulkUnapproveModalRows = (checkboxes, assessData) => {
  const assessmentByUuid = new Map(
    (assessData?.timesheets || []).map((entry) => [entry.timesheet_uuid, entry])
  );

  return (checkboxes || []).map((row) => {
    const assessment = assessmentByUuid.get(row.uuid) || {};

    return {
      uuid: row.uuid,
      workerName: buildWorkerLabelForConfirm(row),
      participants: row.participants || [],
      details: buildTimesheetConfirmDetails(row),
      status: row.status,
      blocked: Boolean(assessment.blocked),
      blockReason: assessment.block_reason || null,
      invoiceNumber: assessment.invoice_number || null,
      eligible: Boolean(assessment.eligible) && !assessment.blocked,
      requiresFundingWarning: Boolean(assessment.requires_funding_warning),
      ambiguousCount: Number(assessment.ambiguous_line_count) || 0,
    };
  });
};

export const getEligibleUnapproveUuids = (modalRows) =>
  (modalRows || [])
    .filter((row) => row.eligible)
    .map((row) => row.uuid);

export const getBulkUnapproveBlockMessage = (blockReason, invoiceNumber) => {
  if (blockReason === "invoice_generated") {
    return invoiceNumber
      ? `Void invoice ${invoiceNumber} before unapproving`
      : "Void the linked invoice before unapproving";
  }
  if (blockReason === "invalid_status") {
    return "This timesheet cannot be unapproved in its current status";
  }
  if (blockReason === "not_found") {
    return "Timesheet could not be found";
  }
  return "Cannot unapprove this timesheet";
};

export const getBulkUnapproveBlockTitle = (blockReason) => {
  if (blockReason === "invoice_generated") return "Invoice linked";
  if (blockReason === "invalid_status") return "Not eligible";
  if (blockReason === "not_found") return "Not found";
  return "Blocked";
};

export const getBulkUnapproveRowOutcome = (row) => {
  if (row?.blocked) return "blocked";
  if (row?.requiresFundingWarning) return "funding_warning";
  return "ready";
};

export const summarizeBulkUnapproveRows = (rows = []) => {
  const summary = { ready: 0, blocked: 0, fundingWarning: 0, total: rows.length };
  rows.forEach((row) => {
    const outcome = getBulkUnapproveRowOutcome(row);
    if (outcome === "blocked") summary.blocked += 1;
    else if (outcome === "funding_warning") summary.fundingWarning += 1;
    else summary.ready += 1;
  });
  return summary;
};
